import axios from 'axios'
import router from '@/router'
import authManager from '@/utils/auth'
import { syncAppSessionFromStorage } from '@/utils/appAuth'
import { goToLogin } from '@/utils/loginFlow'

const CRAWLER_CODE = 431
const AUTH_CODE = 401
const LOGIN_API_SKIP =
  /\/user\/(login|phonecodelogin|emailcodelogin|register|sendphonecode|sendemailcode|captcha)|\/wechat\/|\/security\/captcha/
const AUTH_PAGES = ['/login', '/register', '/wechatcallback', '/officialcallback', '/security/captcha']

let redirectingCaptcha = false
let redirectingLogin = false
let fetchPatched = false

function requestUrlOf(value) {
  if (!value) return ''
  if (typeof value === 'string') return value
  return value.url || value.href || ''
}

/** 未登录 / token 失效。已登录但「没有权限」不按掉登录处理。 */
export function isSessionUnauthorized(data, httpStatus) {
  const status = Number(httpStatus)
  const code = Number(data?.code)
  if (status !== AUTH_CODE && code !== AUTH_CODE) return false
  const msg = String(data?.message || '')
  if (/权限不足|没有权限/.test(msg) && !/未登录|请先登录|Invalid token/.test(msg)) {
    return false
  }
  return true
}

function clearExpiredSession() {
  authManager.logout()
  syncAppSessionFromStorage()
}

export function handleIfUnauthenticated(data, httpStatus, requestUrl = '') {
  if (LOGIN_API_SKIP.test(String(requestUrl))) return false
  if (!isSessionUnauthorized(data, httpStatus)) return false

  clearExpiredSession()

  const route = router.currentRoute?.value
  if (!route) return true
  if (AUTH_PAGES.includes(route.path)) return true

  const needsAuth = route.matched.some((r) => r.meta.requiresAuth)
  if (!needsAuth || redirectingLogin) return true

  redirectingLogin = true
  goToLogin(router, route.fullPath).finally(() => {
    redirectingLogin = false
  })
  return true
}

function patchFetchForAuth() {
  if (fetchPatched || typeof window === 'undefined' || typeof window.fetch !== 'function') return
  fetchPatched = true
  const origFetch = window.fetch.bind(window)
  window.fetch = async (input, init) => {
    const url = requestUrlOf(input)
    const res = await origFetch(input, init)
    if (!String(url).includes('/api/')) return res
    const ct = res.headers.get('content-type') || ''
    if (!ct.includes('json') && res.status !== AUTH_CODE) return res
    try {
      const data = await res.clone().json()
      handleIfUnauthenticated(data, res.status, url)
    } catch {
      handleIfUnauthenticated(null, res.status, url)
    }
    return res
  }
}

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('jwt_token')
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

axios.interceptors.response.use(
  (response) => {
    const code = response?.data?.code
    const url = response?.config?.url || ''
    if (
      code === CRAWLER_CODE &&
      !redirectingCaptcha &&
      !url.includes('/security/captcha/verify') &&
      router.currentRoute.value.path !== '/security/captcha'
    ) {
      redirectingCaptcha = true
      const redirect = router.currentRoute.value.fullPath
      router.push({ path: '/security/captcha', query: { redirect } }).finally(() => {
        redirectingCaptcha = false
      })
    }
    handleIfUnauthenticated(response?.data, response?.status, url)
    return response
  },
  (error) => {
    const res = error?.response
    handleIfUnauthenticated(res?.data, res?.status, error?.config?.url || '')
    return Promise.reject(error)
  }
)

patchFetchForAuth()

export function authHeaders() {
  const token = localStorage.getItem('jwt_token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/** 业务错误文案：handler 通用 "Error while handle call" 时改用 info。 */
export function apiErrorText(data, fallback = '请求失败') {
  if (!data) return fallback
  const msg = String(data.message || '').trim()
  const info = String(data.info || '').trim()
  if (msg === 'Error while handle call' && info) return info
  return msg || info || fallback
}

export { CRAWLER_CODE }
